import { n$2, e$2, c, i$1, u$1 } from '@/js/global/global';
import { Delegate } from '@/js/modules/Delegate'
import { QuantityButtons, CartNoteToggle, CrossSells } from '@/js/global/cart-helpers'
import { animateQuickCart } from '@/js/global/animations'
import { register } from '@/js/global/shopify-theme-sections'
import { createFocusTrap } from '@/js/vendor/focus-tabbable'
import { disableBodyScroll, enableBodyScroll, shouldAnimate, makeRequest, updateInnerHTML } from '@/js/global/helpers'
import { cart } from '@/js/global/cart'
import { freeShippingBar } from '@/js/modules/free-shipping-bar'
import { announce } from '../global/helpers';
import { progressOffersBar } from '../modules/progress-offers';
import { CartEstimate } from '@/js/modules/cart-estimate'
import { reorderExpressCheckoutWallets } from '@/js/modules/express-checkout-order'

const selectors$r = {
  cartWrapper: ".quick-cart__wrapper",
  innerContainer: ".quick-cart__container",
  overlay: ".quick-cart__overlay",
  closeButton: ".quick-cart__close-icon",
  footer: ".quick-cart__footer",
  items: ".quick-cart__items",
  cartError: ".quick-cart__item-error",
  form: ".quick-cart__form",
  cartCount: ".quick-cart__heading sup",
  summaryBody: ".quick-cart__summary-body",
  checkoutTotal: "[data-checkout-total]",
  quantityInput: ".quick-cart .quantity-input__input",
  quantityItem: "[data-input-item]",
  discounts: ".quick-cart__item-discounts",
  freeShippingBar: "[data-free-shipping-bar]",
  freeShippingBarContainer: ".free-shipping-bar__container",
  crossSells: "[data-cross-sells]",
  progressOffers: "[data-progress-offers]",
  progressOffersContainer: ".progress-offers__container",
  expressCheckout: "[data-express-checkout]"
};
const classes$a = {
  active: "active",
  hidden: "hidden",
  updatingQuantity: "has-quantity-update",
  removed: "is-removed"
};
register("quick-cart", {
  onLoad() {
    this.cartWrapper = n$2(selectors$r.cartWrapper, this.container);
    this.cartTrap = createFocusTrap(this.container, {
      allowOutsideClick: true
    });

    // Events are all on events trigger by other components / functions
    this.events = [
      c("quick-cart:open", () => this.openQuickCart()),
      c("quick-cart:updated", () => {
        this.refreshQuickCart();
      }),
      c("quick-cart:error", (_, _ref) => {
        let {
          key,
          errorMessage
        } = _ref;
        this.handleErrorMessage(key, errorMessage);
      }),
      c("quick-cart:scrollup", () => this.scrollUpQuickCart()),
      c(["quantity-update:subtract", "quantity-update:add"], (_, _ref2) => {
        let {
          key
        } = _ref2;
        this.handleQuantityUpdate(key);
      }),
      c("quantity-update:remove", (_, _ref3) => {
        let {
          key
        } = _ref3;
        this.handleItemRemoval(key);
      }),
      e$2(document, "apps:product-added-to-cart", () => {
        this.refreshQuickCart();
      })
    ];
    this.quantityButtons = QuantityButtons(this.container);
    this.cartNoteToggle = CartNoteToggle(this.container);
    if (shouldAnimate(this.container)) {
      this.animateQuickCart = animateQuickCart(this.container);
    }

    // Delegate handles all click events due to rendering different content
    // within quick cart
    this.delegate = new Delegate(this.container);
    this.delegate.on("click", selectors$r.overlay, () => this.close());
    this.delegate.on("click", selectors$r.closeButton, () => this.close());
    this.delegate.on("change", selectors$r.quantityInput, e => this.handleQuantityInputChange(e));
    this.delegate.on("keydown", selectors$r.cartWrapper, e => {
      if (e.code === 'Escape') this.close();
    });
    const freeShippingBar$1 = n$2(selectors$r.freeShippingBar, this.container);
    const progressOffers = n$2(selectors$r.progressOffers, this.container);

    if (freeShippingBar$1) {
      freeShippingBar(freeShippingBar$1);
    }

    if (progressOffers) {
      progressOffersBar(progressOffers);
    }

    this._initCrossSells();

    // ROC - force the express checkout wallet order (Shopify renders the buttons on its
    // own on page load, so only the reorder is armed here; see _initExpressCheckout)
    this.unloadExpressCheckoutOrder = reorderExpressCheckoutWallets(n$2(selectors$r.expressCheckout, this.container));

    // ROC - estimated shipping & taxes in the order summary
    this.cartEstimate = CartEstimate(this.container);

    // ROC-399 - ordergroove cart offers
    // Hooks OG cart updated to cart refresh
    document.addEventListener('og-cart-updated', ev => {
      this.refreshQuickCart();
      ev.preventDefault();
    });
  },
  openQuickCart() {
    var _this$animateQuickCar;
    u$1(this.cartWrapper, classes$a.active);
    this.cartEstimate.refresh();
    this.cartTrap.activate();
    this.adjustItemPadding();
    (_this$animateQuickCar = this.animateQuickCart) === null || _this$animateQuickCar === void 0 ? void 0 : _this$animateQuickCar.open();
    document.body.setAttribute("data-fluorescent-overlay-open", "true");
    // ROC - lets CSS hide third-party floating widgets (Gorgias chat) while the drawer is open
    document.body.setAttribute("data-quick-cart-open", "true");
    disableBodyScroll(this.container, {
      allowTouchMove: el => {
        while (el && el !== document.body) {
          if (el.getAttribute("data-scroll-lock-ignore") !== null) {
            return true;
          }
          el = el.parentNode;
        }
      },
      reserveScrollBarGap: true
    });
  },
  refreshQuickCart() {
    const url = "".concat(window.theme.routes.cart.base, "?section_id=").concat(this.id);
    makeRequest("GET", url).then(response => {
      var _this$crossSells;

      let container = document.createElement("div");
      container.innerHTML = response;

      const responseInnerContainer = n$2(selectors$r.innerContainer, container);
      const cartHasItems = Boolean(n$2(selectors$r.items, this.container));
      const responseHasItems = Boolean(n$2(selectors$r.items, container));
      const freeShippingBar$1 = n$2(selectors$r.freeShippingBar, container);
      const progressOffers = n$2(selectors$r.progressOffers, container);

      (_this$crossSells = this.crossSells) === null || _this$crossSells === void 0 ? void 0 : _this$crossSells.unload();

      if (freeShippingBar$1) {
        freeShippingBar(freeShippingBar$1);
      }

      if (progressOffers) {
        progressOffersBar(progressOffers);
      }

      // Cart has items and needs to update them
      if (responseHasItems && cartHasItems) {
        var _this$animateQuickCar2;

        // Render newly located free shipping (ROC-239) (it used to be located inside the 'Render cart items' container (next lines))
        updateInnerHTML("".concat(selectors$r.cartWrapper, " ").concat(selectors$r.freeShippingBarContainer, " "), container);

        updateInnerHTML("".concat(selectors$r.cartWrapper, " ").concat(selectors$r.progressOffersContainer, " "), container);

        // Render cart items
        updateInnerHTML("".concat(selectors$r.cartWrapper, " ").concat(selectors$r.items), container);
        this.adjustItemPadding();

        // Render cart count
        updateInnerHTML("".concat(selectors$r.cartWrapper, " ").concat(selectors$r.cartCount), container);

        // Render order summary rows and the total in the checkout button
        updateInnerHTML("".concat(selectors$r.cartWrapper, " ").concat(selectors$r.summaryBody), container);
        updateInnerHTML("".concat(selectors$r.cartWrapper, " ").concat(selectors$r.checkoutTotal), container);

        // Render promotions
        updateInnerHTML("".concat(selectors$r.cartWrapper, " ").concat(selectors$r.discounts), container);

        // Handle form scroll state
        const form = n$2(selectors$r.form, this.container);
        const previousScrollPosition = form.scrollTop || 0;
        form.scrollTop = previousScrollPosition;
        (_this$animateQuickCar2 = this.animateQuickCart) === null || _this$animateQuickCar2 === void 0 ? void 0 : _this$animateQuickCar2.setup();

        form.focus({preventScroll: true})
      } else {
        // Cart needs to render empty from having items, or needs to render
        // items from empty state
        const innerContainer = n$2(selectors$r.innerContainer, this.container);
        innerContainer.innerHTML = responseInnerContainer.innerHTML;
        innerContainer.setAttribute('tabindex', '-1')
        innerContainer.focus()
      }
      this._initCrossSells();
      this._initExpressCheckout();
      this.cartEstimate.refresh();
    });
  },

  _initExpressCheckout() {
    if (this.unloadExpressCheckoutOrder) {
      this.unloadExpressCheckoutOrder();
      this.unloadExpressCheckoutOrder = null;
    }
    const container = n$2(selectors$r.expressCheckout, this.container);
    if (!container) return;
    this.unloadExpressCheckoutOrder = reorderExpressCheckoutWallets(container);
    if (window.Shopify && window.Shopify.PaymentButton && typeof window.Shopify.PaymentButton.init === "function") {
      window.Shopify.PaymentButton.init();
    }
  },
  handleErrorMessage(key) {
    const item = n$2("[data-key=\"".concat(key, "\"]"), this.container);
    i$1(n$2(selectors$r.cartError, item), classes$a.hidden);
    i$1(item, classes$a.updatingQuantity);
  },
  handleQuantityUpdate(key) {
    const item = n$2("[data-key=\"".concat(key, "\"]"), this.container);
    u$1(item, classes$a.updatingQuantity);
    announce('Quantity updated')
  },
  handleItemRemoval(key) {
    const item = n$2("[data-key=\"".concat(key, "\"]"), this.container);
    u$1(item, classes$a.removed);
    u$1(item, classes$a.updatingQuantity);
  },
  handleQuantityInputChange(_ref4) {
    let {
      target
    } = _ref4;
    const item = target.closest(selectors$r.quantityItem);
    const {
      key
    } = item.dataset;
    cart.updateItem(key, target.value);
    this.handleQuantityUpdate(key);
  },
  _initCrossSells() {
    const crossSells = n$2(selectors$r.crossSells, this.container);
    if (crossSells) {
      this.crossSells = CrossSells(crossSells);
    }
  },
  scrollUpQuickCart() {
    const form = n$2(selectors$r.form, this.container);
    const previousScrollPosition = 0;
    if (!form) return;
    // delay the scroll up to make it seem more 'fluid'
    setTimeout(() => {
      form.scrollTop = previousScrollPosition;
      form.focus({preventScroll: true})
    }, 300);
  },
  adjustItemPadding() {
    const items = n$2(selectors$r.items, this.container);
    if (!items) return;

    // Ensure cart items accounts for the height of cart footer
    const footer = n$2(selectors$r.footer, this.container);
    items.style.setProperty('--scroll-bottom', `${footer.clientHeight}px`);
  },
  close() {
    i$1(this.cartWrapper, classes$a.active);
    document.body.setAttribute("data-quick-cart-open", "false");
    setTimeout(() => {
      var _this$animateQuickCar3;
      (_this$animateQuickCar3 = this.animateQuickCart) === null || _this$animateQuickCar3 === void 0 ? void 0 : _this$animateQuickCar3.close();
      this.cartTrap.deactivate();
      document.body.setAttribute("data-fluorescent-overlay-open", "false");
      enableBodyScroll(this.container);
    }, 500);
  },
  onSelect() {
    this.openQuickCart();
  },
  onDeselect() {
    this.close();
  },
  onUnload() {
    this.delegate.off();
    this.events.forEach(unsubscribe => unsubscribe());
    this.quantityButtons.unload();
    this.cartNoteToggle.unload();
    this.cartEstimate.unload();
    if (this.unloadExpressCheckoutOrder) this.unloadExpressCheckoutOrder();
  }
});
